import React from 'react';
import BoltIcon              from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterDropIcon           from '@mui/icons-material/WaterDrop';
import ReceiptIcon             from '@mui/icons-material/Receipt';
import type { SvgIconComponent } from '@mui/icons-material';
import type { KpiData } from './types';

const ACCENT_COLORS: Record<string, string> = {
  electricity: '#1D9E75',
  gas:         '#BA7517',
  water:       '#378ADD',
  cost:        '#D4537E',
};

const TREND_COLORS: Record<string, string> = {
  down: '#0F6E56',
  up:   '#993C1D',
  good: '#0F6E56',
};

const ICONS: Record<string, SvgIconComponent> = {
  electricity: BoltIcon,
  gas:         LocalFireDepartmentIcon,
  water:       WaterDropIcon,
  cost:        ReceiptIcon,
};

interface Props extends KpiData {
  label: string;
}

export default function KpiCard({ id, label, value, subLabel, trend }: Props) {
  const accentColor = ACCENT_COLORS[id] ?? '#1D9E75';
  const trendColor  = trend ? (TREND_COLORS[trend.direction] ?? '#888780') : undefined;
  const Icon        = ICONS[id];

  return (
    <div style={styles.card}>
      <p style={styles.label}>
        {Icon && <Icon aria-hidden="true" sx={{ fontSize: 14 }} />}
        {label}
      </p>
      <p style={styles.value}>{value}</p>
      <div style={styles.footer}>
        <span style={styles.subLabel}>{subLabel}</span>
        {trend && (
          <span style={{ ...styles.trend, color: trendColor }}>{trend.label}</span>
        )}
      </div>
      <div style={{ ...styles.accent, background: accentColor }} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    position: 'relative',
    background: 'var(--color-bg-secondary, #f5f5f3)',
    borderRadius: 12,
    padding: '1rem 1.1rem 1.2rem',
    overflow: 'hidden',
    fontFamily: "'Syne', sans-serif",
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--color-text-secondary, #5F5E5A)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  value: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 26,
    fontWeight: 400,
    color: 'var(--color-text-primary, #2C2C2A)',
    lineHeight: 1,
    margin: 0,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.45rem',
  },
  subLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--color-text-secondary, #5F5E5A)',
  },
  trend: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
  },
  accent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
  },
};
